import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { allStates, STANDARD_DATE_FORMAT } from '@et/typings';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
declare const parseFullName: ParseFullName;
declare const addresser: Adresser;

interface ParseFullName {
  parse: (
    nameToParse: string,
    partToReturn?: partToReturn,
    fixCase?: boolean | -1 | 0 | 1,
    stopOnError?: boolean | 0 | 1,
    useLongLists?: boolean | 0 | 1,
  ) => Name;
}

type partToReturn =
  | 'title'
  | 'first'
  | 'middle'
  | 'last'
  | 'nick'
  | 'suffix'
  | 'error'
  | 'all';

interface Name {
  title?: string | undefined;
  first?: string | undefined;
  middle?: string | undefined;
  last?: string | undefined;
  nick?: string | undefined;
  suffix?: string | undefined;
  error?: [] | undefined;
}

interface Adresser {
  parseAddress: (addressString: string) => IParsedAddress;
}

interface IParsedAddress {
  zipCode: string;
  stateAbbreviation: string;
  stateName: string;
  placeName: string;
  addressLine1: string;
  addressLine2?: string;
  streetNumber: string;
  streetSuffix: string;
  streetName: string;
  id: string;
  errorMsg?: string;
}

/**
 * Parses address
 * @returns Parsed address
 *
 */
export function parseAddress(value: string) {
  const address = addresser.parseAddress(value);
  if (address.errorMsg) {
    return {
      zipCode: address.errorMsg,
      stateAbbreviation: address.errorMsg,
      stateName: address.errorMsg,
      placeName: address.errorMsg,
      addressLine1: address.errorMsg,
      addressLine2: address.errorMsg,
      streetNumber: address.errorMsg,
      streetSuffix: address.errorMsg,
      streetName: address.errorMsg,
      id: address.errorMsg,
      errorMsg: address.errorMsg,
    };
  }
  return address;
}

/**
 * Parses full name
 * @returns A string with numbers
 *
 */
export function parseName(name: string) {
  return parseFullName.parse(name);
}

/**
 * Adds decimals to string
 * @returns A string
 *
 */
export function addDecimal(
  value: string,
  decimal: number = 2,
  maxNum?: number,
) {
  if (!value) {
    return '';
  }
  if (Number.isNaN(Number(value))) {
    return 'Number is invalid';
  }
  if (maxNum && Number(value) > maxNum) {
    return 'Max value is ' + maxNum;
  }
  return Number(value).toFixed(decimal).toString();
}

/**
 * Remove all characters which is not a number
 * @returns A string with numbers
 *
 */
export function onlyDigits(value: string, maxLength?: number) {
  const parsedValue = value.replace(/[^0-9]/g, '');
  if (maxLength) {
    return parsedValue.slice(0, maxLength);
  }
  return parsedValue;
}

/**
 * Remove characters $ and , and %
 * @returns A string without following characters $ and , and %
 *
 */
export function removeDollarSignCommaPercentage(value: string) {
  return value.replace(/[^0-9.]|,|%/g, '');
}

/**
 * Remove all end of line symbols. Optionally add string to replave with
 * @returns String
 *
 */
export function removeEndOfLine(value: string, replaceWith: string = '') {
  return value.replace(/[\r\n]/gm, replaceWith);
}

/**
 * Format date
 * @returns A string with numbers
 *
 */
export function transformDate(
  value: string,
  dateFormat: string = 'YYYY-MM-DD',
) {
  return dayjs(new Date(value)).format(dateFormat);
}

/**
 * "Get the current UTC date and format it as a string."
 *
 * @returns A string in the format of YYYY-MM-DDTHH:mm:ssZ
 */
export function getCurrentUTCDate() {
  return dayjs.utc().format();
}

/**
 * Converts a date to UTC date
 * @param date - The date to convert
 * @returns A string in the format of YYYY-MM-DDTHH:mm:ssZ
 */
export function convertToUTCDate(date: string) {
  return dayjs(date).utc().format();
}

/**
 * Converts an `ng2-date-picker` control value (typically `Date` or display-format string)
 * to a UTC ISO-8601 string for **local calendar** of that date.
 *
 * @param value - Value from the date picker (e.g. `Date` or `MMMM DD, YYYY` string)
 * @param stringFormat - `dayjs` parse format when `value` is a string (default: `STANDARD_DATE_FORMAT`)
 * @param endOfDay - When `true`, returns end of day (23:59:59.999) to include the full day; otherwise start of day (midnight)
 * @returns ISO string (e.g. `2026-03-26T...Z`) or `undefined` if empty/invalid
 */
export function datePickerValueToUtcIso(
  value: unknown,
  stringFormat: string = STANDARD_DATE_FORMAT,
  endOfDay = false,
): string | undefined {
  if (value == null || value === '') {
    return undefined;
  }

  let localDay: dayjs.Dayjs;

  if (value instanceof Date) {
    localDay = dayjs(value);
  } else if (typeof value === 'string') {
    const strict = dayjs(value, stringFormat, true);
    localDay = strict.isValid() ? strict : dayjs(value);
  } else if (typeof value === 'number') {
    localDay = dayjs(value);
  } else {
    return undefined;
  }

  if (!localDay.isValid()) {
    return undefined;
  }

  const normalized = endOfDay ? localDay.endOf('day') : localDay.startOf('day');
  return normalized.toDate().toISOString();
}

/**
 * Finds state in provided string and return state name and abbreviation
 * @returns An object with state name and abbreviation
 *
 */
export function findStateInString(value: string) {
  const stateObj = allStates.find((state) =>
    value.toLocaleLowerCase().includes(state.name.toLocaleLowerCase()),
  );
  return (
    stateObj || {
      name: 'State not found',
      abbreviation: 'State not found',
    }
  );
}

/**
 * Converts a phone number to digits only
 * @param phoneNumber - Phone number in any format
 * @returns Phone number with only digits
 */
export function phoneToDigits(phoneNumber: string | undefined): string {
  if (!phoneNumber) {
    return '';
  }
  return phoneNumber.replace(/\D/g, '');
}

/**
 * Converts a phone number to E.164 for the API (e.g. `(402) 430-7354` -> `+14024307354`).
 * 10-digit and 11-digit `1...` US numbers get `+1`; a value that already starts with `+` keeps
 * its country code. Anything else is returned as digits only so the API can reject it.
 * @param phoneNumber - Phone number in any format
 * @returns E.164 string, or '' when empty
 */
export function phoneToE164(phoneNumber: string | undefined | null): string {
  if (!phoneNumber) {
    return '';
  }
  const digits = phoneNumber.replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  if (phoneNumber.trim().startsWith('+')) {
    return `+${digits}`;
  }
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  return digits;
}

/** E.164: + then 8–15 digits (ITU-T E.164 max length including country code). */
const E164_REGEX = /^\+[1-9]\d{7,14}$/;

/**
 * True when a phone input can be normalized to E.164 (US 10-digit, +country, or
 * masked forms). Empty values are treated as valid so `Validators.required` can
 * own emptiness separately.
 */
export function isValidE164PhoneInput(
  phoneNumber: string | undefined | null,
): boolean {
  if (!phoneNumber || String(phoneNumber).trim() === '') {
    return true;
  }
  return E164_REGEX.test(phoneToE164(String(phoneNumber)));
}

/**
 * Converts a stored phone (digits or E.164, e.g. `+14024307354`) to the US display format
 * (XXX) XXX-XXXX. A leading US country code `1` on an 11-digit value is dropped.
 * Values that are not US numbers are returned as digits only.
 * @param digits - Phone number digits or E.164 string
 * @returns Formatted phone number as (XXX) XXX-XXXX
 */
export function digitsToPhoneFormat(digits: string | undefined): string {
  if (!digits) {
    return '';
  }
  let cleaned = digits.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    cleaned = cleaned.slice(1);
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6,
    )}`;
  }
  return cleaned;
}
